// pages/MasterJetty.jsx
'use client';

import { useState, useEffect, useMemo } from 'react';
import DataTable from '../../../components/DataTable';
import { Chip, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, IconButton, Box } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { checkIsUsedInRouteTrip } from '../../../stores/routeTripStore';
import { useTransitPointList, deleteTransitPoint } from '../../../stores/transitPointStore';
import { useCompanyList } from '../../../stores/companyStore';
import ActionMenu from '../../../components/ActionMenu';
import { useNotifier } from '../../../components/Notifier';
import DeleteConfirmDialog from '../../../components/DeleteConfirmDialog';
import MasterJettyAddEdit from './MasterJettyAddEdit';
import { fleetApi } from '../../../api/axiosClient';
import { useLanguage } from '../../../utils/i18n';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

function MasterJettyContent() {
  const navigate = useNavigate();
  const currentUserRole = typeof window !== 'undefined' ? localStorage.getItem('rolename') || localStorage.getItem('role_name') || '' : '';

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [searchField, setSearchField] = useState('name');
  const [searchRaw, setSearchRaw] = useState('');
  const [searchValue, setSearchValue] = useState('');

  const [localPoints, setLocalPoints] = useState([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [localTotal, setLocalTotal] = useState(0);

  const { companyOptions } = useCompanyList();
  const notify = useNotifier();
  const { t } = useLanguage();

  const searchFieldOptions = useMemo(
    () => [
      { value: 'all', label: t('general.searchAll', 'All') },
      { value: 'name', label: t('masterJetty.name', 'Jetty Name'), fieldName: 'name' },
      { value: 'code', label: t('masterJetty.code', 'Jetty Code'), fieldName: 'code' },
      {
        value: 'customer',
        label: t('masterJetty.customerName', 'Customer'),
        tableName: 'm_customer',
        fieldName: 'name',
        joinKey: 'customer'
      },
      { value: 'min_speed', label: t('masterJetty.minSpeed', 'Min Speed') },
      { value: 'max_speed', label: t('masterJetty.maxSpeed', 'Max Speed') },
      { value: 'pic_name', label: t('masterJetty.picName', 'PIC Name'), fieldName: 'pic_name' },
      { value: 'pic_phone', label: t('masterJetty.picPhone', 'PIC Phone'), fieldName: 'pic_phone' }
    ],
    [t]
  );

  const [selectedCompanies, setSelectedCompanies] = useState(() => {
    try {
      const stored = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
      return stored ? [{ value: String(stored), label: String(stored) }] : [];
    } catch {
      return [];
    }
  });

  const companyOptionsNormalized = useMemo(
    () =>
      (companyOptions || []).map((c) => ({
        value: String(c.value ?? c.id ?? c),
        label: c.label ?? c.name ?? String(c.value ?? c.id ?? c)
      })),
    [companyOptions]
  );

  useEffect(() => {
    try {
      if (!companyOptionsNormalized || companyOptionsNormalized.length === 0) return;
      const raw = typeof window !== 'undefined' ? localStorage.getItem('user') : null;
      if (!raw) return;
      let u = null;
      try {
        u = JSON.parse(raw);
      } catch {
        return;
      }
      const companyId = u?.company ?? u?.company_id ?? null;
      if (companyId == null) return;
      const companyIdStr = String(companyId);
      const match = companyOptionsNormalized.find((o) => String(o.value) === companyIdStr);
      const selected = match
        ? [{ value: String(match.value), label: match.label ?? String(match.value) }]
        : [{ value: companyIdStr, label: companyIdStr }];
      setSelectedCompanies((prev) => {
        const prevVal = prev && prev[0] && String(prev[0].value);
        if (prevVal === selected[0].value) return prev;
        return selected;
      });
    } catch (e) {
      console.error('auto select company from user failed', e);
    }
  }, [companyOptionsNormalized]);

  const companyFilterColumns = useMemo(() => {
    let storedCompanyId = null;
    try {
      if (typeof window !== 'undefined') storedCompanyId = localStorage.getItem('companyId');
    } catch {
      storedCompanyId = null;
    }

    if (selectedCompanies && selectedCompanies.length > 0) {
      const hasAll = selectedCompanies.some((s) => String(s.value) === 'all');
      if (hasAll) return [];
      const values = selectedCompanies.map((s) => String(s.value));
      if (values.length === 1) {
        return [{ name: 'company_id', logic_operator: '=', operator: 'AND', value: values[0] }];
      } else {
        return [{ name: 'company_id', logic_operator: 'in', operator: 'AND', value: values.join(',') }];
      }
    }

    if (storedCompanyId && String(storedCompanyId) !== 'all') {
      return [{ name: 'company_id', logic_operator: '=', operator: 'AND', value: String(storedCompanyId) }];
    }

    return [];
  }, [selectedCompanies]);

  const filterCompanyIds = useMemo(() => {
    if (!selectedCompanies || selectedCompanies.length === 0) return '';
    const hasAll = selectedCompanies.some((s) => String(s.value) === 'all');
    if (hasAll) return '';
    return selectedCompanies.map((s) => String(s.value)).join(',');
  }, [selectedCompanies]);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearchValue(String(searchRaw ?? '').trim());
      setPaginationModel((p) => ({ ...p, page: 0 }));
    }, 700);

    return () => clearTimeout(t);
  }, [searchRaw, searchField]);

  const hookSearchField = searchField === 'all' ? '' : searchField;
  const hookSearchValue = searchField === 'all' ? '' : searchValue;

  const {
    data: transitPointsFromHook,
    isLoading: hookLoading,
    totalRecord: hookTotal
  } = useTransitPointList(paginationModel.page, paginationModel.pageSize, hookSearchField, hookSearchValue, filterCompanyIds);

  const rows = searchField === 'all' ? localPoints : transitPointsFromHook || [];
  const isLoading = searchField === 'all' ? localLoading : hookLoading;
  const totalRecords = searchField === 'all' ? localTotal : hookTotal || 0;

  useEffect(() => {
    if (searchField !== 'all') return;
    const controller = new AbortController();

    const fetchAllSearch = async () => {
      setLocalLoading(true);
      try {
        const start = paginationModel.page * paginationModel.pageSize;
        const length = paginationModel.pageSize;

        const columns = [...companyFilterColumns];
        if (columns.length === 0) {
          columns.push({ logic_operator: 'like', name: 'name', operator: 'AND', value: '' });
        }
        let filters = [];
        if (searchValue) {
          filters = [
            { name: 'name', table_name: 'm_company', value: searchValue },
            { name: 'name', value: searchValue },
            { name: 'code', value: searchValue },
            { name: 'name', table_name: 'm_customer', value: searchValue },
            { name: 'pic_name', value: searchValue },
            { name: 'pic_phone', value: searchValue },
            { name: 'min_speed', value: searchValue },
            { name: 'max_speed', value: searchValue }
          ];
        }

        const payload = {
          paging: { start, length },
          orders: { columns: ['name'], ascending: true },
          joins: [
            { name: 'company', column_results: ['name'] },
            { name: 'customer', column_results: ['name'] }
          ],
          columns: columns,
          filters: filters
        };

        const resp = await fleetApi.post('/transit-point/search' + getTzParam(), payload, { signal: controller.signal });
        const data = resp?.data ?? null;

        const fetchedRows = Array.isArray(data) ? data : (data?.data ?? []);
        const total = Array.isArray(data) ? fetchedRows.length : (data?.totalRecord ?? data?.totalRecords ?? data?.dataSize ?? 0);

        setLocalPoints(fetchedRows);
        setLocalTotal(total);
      } catch (err) {
        if (err.name !== 'AbortError') console.error('Transit Point search all error', err);
      } finally {
        if (!controller.signal.aborted) setLocalLoading(false);
      }
    };

    fetchAllSearch();
    return () => controller.abort();
  }, [searchField, searchValue, paginationModel, companyFilterColumns]);

  const handleFetchAllForExport = async () => {
    try {
      const length = totalRecords > 0 ? totalRecords : 9999;
      const columns = [...companyFilterColumns];
      if (columns.length === 0) {
        columns.push({ logic_operator: 'like', name: 'name', operator: 'AND', value: '' });
      }

      let filters = [];
      if (searchValue) {
        filters = [
          { name: 'name', table_name: 'm_company', value: searchValue },
          { name: 'name', value: searchValue },
          { name: 'code', value: searchValue },
          { name: 'name', table_name: 'm_customer', value: searchValue },
          { name: 'pic_name', value: searchValue },
          { name: 'pic_phone', value: searchValue },
          { name: 'min_speed', value: searchValue },
          { name: 'max_speed', value: searchValue }
        ];
      }

      const payload = {
        paging: { start: 0, length },
        orders: { columns: ['name'], ascending: true },
        joins: [
          { name: 'company', column_results: ['name'] },
          { name: 'customer', column_results: ['name'] }
        ],
        columns: columns,
        filters: filters
      };

      const resp = await fleetApi.post('/transit-point/search' + getTzParam(), payload);
      const data = resp?.data ?? null;
      return Array.isArray(data) ? data : (data?.data ?? []);
    } catch (err) {
      console.error('Transit Point export all error', err);
      return [];
    }
  };

  const handlePageChange = (model) => {
    setPaginationModel((prev) => {
      if (prev.page === model.page && prev.pageSize === model.pageSize) return prev;
      return model;
    });
  };

  const handleAddClick = () => {
    setEditingId(null);
    setOpenDialog(true);
  };

  const handleEditClick = (id) => {
    setEditingId(id);
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setEditingId(null);
  };

  const handleCompanyChange = (options = []) => {
    const sanitized = (options || []).map((o) => ({ value: String(o.value), label: o.label ?? String(o.value) }));
    setSelectedCompanies(sanitized);
    try {
      if (typeof window !== 'undefined') {
        if (sanitized.length === 1 && sanitized[0].value !== 'all') {
          localStorage.setItem('companyId', String(sanitized[0].value));
        } else {
          localStorage.removeItem('companyId');
        }
      }
    } catch {
      console.log('eror');
    }
  };

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTargetId, setConfirmTargetId] = useState(null);
  const [confirmName, setConfirmName] = useState('');
  const [confirmLoading, setConfirmLoading] = useState(false);

  const [warningOpen, setWarningOpen] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');

  const handleDelete = async (id, itemName) => {
    const { isUsed, routeNames } = await checkIsUsedInRouteTrip(id, 'transit_point');
    if (isUsed) {
      setWarningMessage(
        <Box>
          <Typography variant="body1" sx={{ mb: 1.5 }}>
            Data "<strong>{itemName}</strong>" is not allowed to be deleted because it is registered in the following route(s):
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5, maxHeight: 150, overflowY: 'auto', p: 0.5 }}>
            {routeNames.map((r, i) => (
              <Chip key={i} label={r} size="small" color="primary" variant="outlined" sx={{ fontWeight: 600, bgcolor: 'error.50' }} />
            ))}
          </Box>
          <Typography variant="body2" color="text.secondary">
            If you wish to delete it, please update the route first.
          </Typography>
        </Box>
      );
      setWarningOpen(true);
      return;
    }

    setConfirmTargetId(id);
    setConfirmName(itemName || '');
    setConfirmOpen(true);
  };

  const handleConfirmClose = () => {
    if (confirmLoading) return;
    setConfirmOpen(false);
    setConfirmTargetId(null);
    setConfirmName('');
  };

  const handleConfirmDelete = async () => {
    if (!confirmTargetId) return;
    setConfirmLoading(true);
    try {
      await deleteTransitPoint(confirmTargetId);
      notify({ message: t('notify.deleted', 'Deleted successfully'), severity: 'success' });
      setPaginationModel((p) => ({ ...p }));
    } catch (err) {
      console.error(err);
      notify({ message: t('notify.deleteFailed', 'Failed to delete'), severity: 'error' });
    } finally {
      setConfirmLoading(false);
      setConfirmOpen(false);
      setConfirmTargetId(null);
      setConfirmName('');
    }
  };

  const handleSearch = ({ field, value }) => {
    const safeField = typeof field === 'object' ? field?.value : field;
    setSearchField(safeField || 'all');
    setSearchRaw(value ?? '');
    setPaginationModel((p) => ({ ...p, page: 0 }));
  };

  const safeJoin = (row, path, fallback = '-') => {
    try {
      return path.split('.').reduce((acc, k) => (acc ? acc[k] : undefined), row) ?? fallback;
    } catch {
      return fallback;
    }
  };

  const columns = [
    {
      field: 'company',
      headerName: t('masterJetty.company', 'Company'),
      flex: 1.2,
      valueGetter: (params, row) => row?.join?.company_name || '-'
    },
    { field: 'name', headerName: t('masterJetty.name', 'Jetty Name'), flex: 2 },
    { field: 'code', headerName: t('masterJetty.code', 'Jetty Code'), flex: 1 },
    {
      field: 'customer',
      headerName: t('masterJetty.customerName', 'Customer Name'),
      flex: 2,
      renderCell: (params) => safeJoin(params.row, 'join.customer_name', '-')
    },
    {
      field: 'min_speed',
      headerName: t('masterJetty.minSpeed', 'Min Speed'),
      flex: 1,
      valueGetter: (value, params) => (params.min_speed ? `${params.min_speed} km/h` : '-')
    },
    {
      field: 'max_speed',
      headerName: t('masterJetty.maxSpeed', 'Max Speed'),
      flex: 1,
      valueGetter: (value, params) => (params.max_speed ? `${params.max_speed} km/h` : '-')
    },
    { field: 'pic_name', headerName: t('masterJetty.picName', 'PIC Name'), flex: 1 },
    { field: 'pic_phone', headerName: t('masterJetty.picPhone', 'PIC Phone'), flex: 1 },
    {
      field: 'status',
      headerName: t('masterJetty.status', 'Status'),
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      valueGetter: (value, row) => {
        const st = row?.status ?? row?.is_active ?? null;
        if (st === null || st === undefined) return '-';
        if (typeof st === 'boolean') return st ? 'Active' : 'Not Active';
        return String(st);
      },
      renderCell: (params) => {
        const isActive = params.row?.status ?? false;
        return isActive ? (
          <Chip label={t('general.active', 'Active')} color="success" variant="outlined" size="small" />
        ) : (
          <Chip label={t('general.notActive', 'Not Active')} color="error" variant="outlined" size="small" />
        );
      }
    },
    { field: 'geom', headerName: t('polygonType.geom', 'Geom'), hide: true },
    { field: 'centroid_lat', headerName: t('polygonType.centroidLatitude', 'Centroid Latitude'), hide: true },
    { field: 'centroid_lon', headerName: t('polygonType.centroidLongitude', 'Centroid Longitude'), hide: true },

    ...(currentUserRole === 'Administrator'
      ? [
          { field: 'created_by', headerName: t('masterCompany.createdBy', 'Created By'), flex: 0.5, hide: true },
          { field: 'created_date', headerName: t('masterCompany.createdDate', 'Created Date'), flex: 0.5, hide: true },
          { field: 'modified_by', headerName: t('masterCompany.modifiedBy', 'Modified By'), flex: 0.5, hide: true },
          { field: 'modified_date', headerName: t('masterCompany.modifiedDate', 'Modified Date'), flex: 0.5, hide: true }
        ]
      : []),
    {
      field: 'actions',
      headerName: t('general.actions', 'Actions'),
      sortable: false,
      width: 120,
      headerAlign: 'center',
      renderCell: (params) => (
        <ActionMenu row={params.row} onEdit={handleEditClick} onDelete={(id) => handleDelete(id, params.row.name)} mode="inline" />
      )
    }
  ];

  return (
    <div>
      <DataTable
        title={t('masterJetty.title', 'Master Jetty')}
        onFetchAllForExport={handleFetchAllForExport}
        rows={rows}
        columns={columns}
        loading={isLoading}
        pageSizeOptions={[10, 25, 50]}
        paginationMode="server"
        rowCount={totalRecords}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePageChange}
        searchQuery={searchRaw}
        onSearch={({ field, value }) => handleSearch({ field, value })}
        searchFieldOptions={searchFieldOptions}
        selectedSearchField={searchField}
        onSearchFieldChange={(f) => {
          const safeField = typeof f === 'object' ? f?.value : f;
          setSearchField(safeField || 'all');
          setSearchRaw('');
          setSearchValue('');
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        onAddClick={handleAddClick}
        searchLabel={t('general.searchByName', 'Search by Name')}
        companyOptions={companyOptionsNormalized}
        selectedCompanies={selectedCompanies}
        onCompanyChange={handleCompanyChange}
      />

      <MasterJettyAddEdit
        open={openDialog}
        id={editingId}
        companyOptions={companyOptionsNormalized}
        onClose={handleDialogClose}
        onSuccess={(msg) => {
          setPaginationModel((p) => ({ ...p }));
          handleDialogClose();
        }}
        onError={(msg) => {
          notify({ message: msg || t('notify.operationFailed', 'Operation failed'), severity: 'error' });
        }}
      />

      <Dialog
        open={warningOpen}
        onClose={() => setWarningOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2, p: 0, overflow: 'visible', boxShadow: '0 10px 30px rgba(8,14,30,0.12)' }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, rgba(255, 193, 7, 0.12), rgba(255, 152, 0, 0.08))',
                border: '1px solid rgba(255,152,0,0.18)'
              }}
            >
              <WarningAmberIcon sx={{ color: '#ff9800', fontSize: 28 }} />
            </Box>
            <DialogTitle sx={{ p: 0, fontWeight: 700 }}>Action Denied</DialogTitle>
          </Box>
          <IconButton onClick={() => setWarningOpen(false)} size="small" sx={{ color: 'text.secondary' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent dividers sx={{ pt: 2, pb: 2 }}>
          {typeof warningMessage === 'string' ? (
            <Typography variant="body1" sx={{ mb: 1.25 }}>
              {warningMessage}
            </Typography>
          ) : (
            warningMessage
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setWarningOpen(false);
              navigate('/master-route');
            }}
            sx={{
              textTransform: 'none',
              borderRadius: 1.5,
              px: 2.5,
              fontWeight: 600
            }}
          >
            Master Route
          </Button>
          <Button
            variant="contained"
            onClick={() => setWarningOpen(false)}
            sx={{
              textTransform: 'none',
              borderRadius: 1.5,
              px: 2.5,
              fontWeight: 600,
              boxShadow: '0 6px 18px rgba(0,0,0,0.08)'
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteConfirmDialog
        open={confirmOpen}
        loading={confirmLoading}
        onClose={handleConfirmClose}
        onConfirm={handleConfirmDelete}
        title={t('deleteConfirm.title', 'Confirm Delete')}
        message={t('deleteConfirm.message', 'Are you sure you want to delete this item?')}
        confirmLabel={t('deleteConfirm.label', 'Delete')}
        cancelLabel={t('deleteConfirm.cancel', 'Cancel')}
        itemName={confirmName}
      />
    </div>
  );
}

export default function MasterJetty() {
  return (
    <>
      <MasterJettyContent />
    </>
  );
}
